import { z } from "zod";
/*
 * Product → NCM search. Reference only: the official Siscomex/TIPI text is
 * returned verbatim, translations are labelled unofficial, and the SP ST hint
 * reports what the active published ruleset already says. Nothing here
 * classifies a product, publishes a rule or prices a calculation.
 */
export const NCM_SEARCH_LANGUAGES = ["pt", "es", "en"];
export const ncmSearchLanguageSchema = z.enum(NCM_SEARCH_LANGUAGES);
export const NCM_SEARCH_MAX_LIMIT = 25;
export const NCM_SEARCH_DEFAULT_LIMIT = 10;
export const NCM_SEARCH_QUERY_MAX = 120;
/** Levels of the Mercosur nomenclature, by code length (2, 4, 5–6, 7, 8 digits). */
export const ncmLevelSchema = z.enum(["chapter", "heading", "subheading", "item", "subitem"]);
export function ncmLevelForCode(code) {
    if (!/^\d+$/.test(code))
        return null;
    switch (code.length) {
        case 2:
            return "chapter";
        case 4:
            return "heading";
        case 5:
        case 6:
            return "subheading";
        case 7:
            return "item";
        case 8:
            return "subitem";
        default:
            return null;
    }
}
/**
 * Folds text for search: strips markup, accents and punctuation, lowercases,
 * collapses spaces. "Xampús", "XAMPU" and "xampu" all become "xampus"/"xampu".
 * The same function builds the index and reads the query, so they always agree.
 */
export function normalizeNcmSearchText(value) {
    return value
        .replace(/<[^>]*>/g, " ")
        .replace(/&[a-z]+;|&#\d+;/gi, " ")
        .normalize("NFD")
        .replace(/\p{M}+/gu, "")
        .toLowerCase()
        .replace(/ß/g, "ss")
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim()
        .replace(/\s+/g, " ");
}
/** Display form of an official description: markup and hierarchy dashes removed, text kept. */
export function displayNcmDescription(value) {
    return value
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/^\s*(?:[-–]\s*)+/u, "")
        .replace(/\s+/g, " ")
        .trim();
}
/** A query that is only digits, dots and spaces is a code lookup, not a product name. */
export function ncmSearchCodeQuery(value) {
    const trimmed = value.trim();
    if (!/^[\d.\s-]+$/.test(trimmed))
        return null;
    const digits = trimmed.replace(/\D/g, "");
    return digits.length >= 2 && digits.length <= 8 ? digits : null;
}
export const ncmSearchQuerySchema = z
    .object({
    q: z.string().trim().min(2).max(NCM_SEARCH_QUERY_MAX),
    lang: ncmSearchLanguageSchema.default("pt"),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(NCM_SEARCH_MAX_LIMIT)
        .default(NCM_SEARCH_DEFAULT_LIMIT),
})
    .strict();
const ncmCodeSchema = z.string().regex(/^\d{8}$/);
export const ncmDescriptionTextSchema = z
    .object({
    /** The text to show in the requested language. */
    text: z.string().min(1),
    language: ncmSearchLanguageSchema,
    /** OFFICIAL is the Siscomex Portuguese text; UNOFFICIAL is a labelled translation. */
    status: z.enum(["OFFICIAL", "UNOFFICIAL"]),
})
    .strict();
export const ncmBreadcrumbSchema = z
    .object({
    code: z.string().regex(/^\d{2,8}$/),
    level: ncmLevelSchema,
    /** Siscomex text, unaltered. */
    officialDescription: z.string().min(1),
    description: ncmDescriptionTextSchema,
})
    .strict();
export const ncmCestReferenceSchema = z
    .object({
    cest: z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/),
    description: z.string().min(1),
    segmentName: z.string().min(1),
})
    .strict();
/**
 * What the active published ruleset says about ICMS-ST into SP for this NCM.
 * ST: a rule in force puts it under ST; NO_ST: rules for SP exist and none is ST;
 * UNKNOWN: no published rule answers SP (not a statement that there is no ST).
 */
export const ncmSpStHintSchema = z
    .object({
    uf: z.literal("SP"),
    status: z.enum(["ST", "NO_ST", "UNKNOWN"]),
    rulesetVersion: z.string().min(1).nullable(),
    /** Published original MVA as a decimal string (e.g. "0.7241"), only when status is ST. */
    originalMva: z.string().min(1).nullable(),
})
    .strict();
export const ncmSearchMatchSchema = z
    .object({
    /** The indexed term that matched, as stored (official text, translation or synonym). */
    term: z.string().min(1),
    kind: z.enum(["CODE", "OFFICIAL", "TRANSLATION", "SYNONYM"]),
    language: ncmSearchLanguageSchema,
    /** The nomenclature level where the term lives (a heading match covers its items). */
    code: z.string().regex(/^\d{2,8}$/),
})
    .strict();
export const ncmEntrySchema = z
    .object({
    ncm: ncmCodeSchema,
    /** Siscomex Portuguese text of the 8-digit code, unaltered. */
    officialDescription: z.string().min(1),
    description: ncmDescriptionTextSchema,
    /** Chapter → heading → subheading → item, excluding the code itself. */
    breadcrumbs: z.array(ncmBreadcrumbSchema).max(6),
    cests: z.array(ncmCestReferenceSchema).max(20),
    spSt: ncmSpStHintSchema,
    legalAct: z.string().nullable(),
})
    .strict();
export const ncmSearchResultSchema = ncmEntrySchema
    .extend({
    /** Relative relevance in [0, 1]; only meaningful within one response. */
    score: z.number().min(0).max(1),
    match: ncmSearchMatchSchema,
})
    .strict();
/** A product description from this organization's own past lots, with the NCM it carried. */
export const ncmHistorySuggestionSchema = z
    .object({
    description: z.string().min(1).max(300),
    ncm: ncmCodeSchema,
    officialDescription: z.string().min(1).nullable(),
    seenCount: z.number().int().positive(),
    lastSeenAt: z.string().datetime(),
})
    .strict();
export const NCM_TRANSLATION_LABEL = "tradução não oficial";
export const ncmSearchResponseSchema = z
    .object({
    query: z.string(),
    language: ncmSearchLanguageSchema,
    items: z.array(ncmSearchResultSchema).max(NCM_SEARCH_MAX_LIMIT),
    /** "Produtos parecidos já classificados" from this organization's lots. */
    history: z.array(ncmHistorySuggestionSchema).max(5),
    catalog: z
        .object({
        /** False until the nomenclature sync has run for this organization. */
        available: z.boolean(),
        tableAct: z.string().nullable(),
        translationLabel: z.literal(NCM_TRANSLATION_LABEL),
        datasetVersion: z.string().nullable(),
    })
        .strict(),
})
    .strict();
export const ncmDetailQuerySchema = z
    .object({ lang: ncmSearchLanguageSchema.default("pt") })
    .strict();
export const ncmDetailResponseSchema = ncmEntrySchema
    .extend({
    status: z.enum(["active", "inactive"]),
    validFrom: z.string().date().nullable(),
    validTo: z.string().date().nullable(),
    source: z.string().min(1),
    sourceUrl: z.string().url(),
    /** Curated everyday names in the requested language (unofficial). */
    commonNames: z.array(z.string().min(1)).max(20),
    translationLabel: z.literal(NCM_TRANSLATION_LABEL),
})
    .strict();
export const NCM_DESCRIPTIONS_MAX_CODES = 200;
/** `codes` is a comma-separated list of 8-digit NCMs (dots allowed). */
export const ncmDescriptionsQuerySchema = z
    .object({
    codes: z
        .string()
        .min(8)
        .max(NCM_DESCRIPTIONS_MAX_CODES * 11)
        .transform((value, context) => {
        const codes = [
            ...new Set(value
                .split(",")
                .map((code) => code.replace(/[.\s]/g, ""))
                .filter(Boolean)),
        ];
        if (codes.length > NCM_DESCRIPTIONS_MAX_CODES ||
            codes.some((code) => !/^\d{8}$/.test(code))) {
            context.addIssue({
                code: "custom",
                message: `Up to ${NCM_DESCRIPTIONS_MAX_CODES} comma-separated 8-digit NCM codes`,
            });
            return z.NEVER;
        }
        return codes;
    }),
    lang: ncmSearchLanguageSchema.default("pt"),
})
    .strict();
export const ncmDescriptionItemSchema = z
    .object({
    ncm: ncmCodeSchema,
    officialDescription: z.string().min(1),
    description: ncmDescriptionTextSchema,
})
    .strict();
export const ncmDescriptionsResponseSchema = z
    .object({
    /** Codes the official table does not carry are simply absent. */
    items: z.array(ncmDescriptionItemSchema).max(NCM_DESCRIPTIONS_MAX_CODES),
    translationLabel: z.literal(NCM_TRANSLATION_LABEL),
})
    .strict();
//# sourceMappingURL=ncm-search.js.map